import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AssignmentReturn as ReturnIcon,
  Print as PrintIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import Button from '@mui/material/Button';
import { StockReturnNew, StockReturnItem } from '../types/stockReturnNew';
import { stockReturnsAPI, auditLogsAPI } from '../services/api';
import {
  useStockReturnsList,
  useDeletedStockReturns,
  useInvalidateStockReturns,
} from '../hooks/queries/useStockReturns';
import { useArticlesDropdown, useClientsDropdown } from '../hooks/queries/useDropdowns';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import { extractErrorMessage } from '../utils/errorHandler';
import usePdfPreview from '../hooks/usePdfPreview';
// generateStockReturnPDF is dynamically imported in handlePrint so the jspdf
// chunk only loads when the user clicks Print.
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import StockReturnAdd from './StockReturnAdd';
import StockReturnEdit from './StockReturnEdit';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';

const STOCK_COLOR = '#2196F3';

const RETURN_FILTER_SCHEMA = {
  date:              { type: 'dateRange', paramFrom: 'start_date', paramTo: 'end_date' },
  bon_retour_number: { type: 'text', param: 'bon_retour_number' },
  client_id:         { type: 'autocomplete', param: 'client_id' },
} as const;

const StockReturnNewManagement: React.FC = () => {
  const { data: articles = [] } = useArticlesDropdown();
  const { data: clients = [] } = useClientsDropdown();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingReturn, setEditingReturn] = useState<StockReturnNew | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [returnToDelete, setReturnToDelete] = useState<StockReturnNew | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredBonNumber, setHoveredBonNumber] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(RETURN_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });
  const clientOptions = clients.map((c) => ({ value: String(c.id), label: c.name }));

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cached + live list: auto-refreshes when any user changes a stock return
  // (WebSocket invalidation), and revisiting the same page/filters is instant.
  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const returnsQuery = useStockReturnsList(listParams, tabValue === 0);
  const deletedQuery = useDeletedStockReturns(tabValue === 1);
  const invalidateReturns = useInvalidateStockReturns();

  const stockReturns = returnsQuery.data?.items ?? [];
  const totalCount = returnsQuery.data?.total ?? 0;
  const deletedStockReturns = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? returnsQuery.isInitialLoading : deletedQuery.isInitialLoading;

  const handleTabChange =(_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleRestore = async (returnEntry: StockReturnNew) => {
    if (!returnEntry.id) return;
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer le retour de stock BON RETOUR "${returnEntry.bon_retour_number}" ?`,
      async () => {
        try {
          await stockReturnsAPI.restore(returnEntry.id!);
          showSuccess('Retour de stock restauré avec succès');
          invalidateReturns();
        } catch (err) {
          showError('Erreur lors de la restauration du retour');
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler'
      }
    );
  };

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD'
    }).format(amount);
  };

  const handleOpenAddDialog = () => {
    setFormMinimized(false);
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (returnEntry: StockReturnNew) => {
    setEditingReturn(returnEntry);
    setOpenEditDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormMinimized(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingReturn(null);
  };

  const handleDeleteClick = (returnEntry: StockReturnNew) => {
    setReturnToDelete(returnEntry);
    setShowDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!returnToDelete || !returnToDelete.id) return;

    setIsDeleting(true);
    try {
      await stockReturnsAPI.delete(returnToDelete.id);
      showSuccess(`BON RETOUR supprimé avec succès (${returnToDelete.items?.length || 0} article(s))`);
      setShowDeleteConfirmDialog(false);
      setReturnToDelete(null);
      invalidateReturns();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const getDeleteConfirmationFields = (): ConfirmationField[] => {
    if (!returnToDelete) return [];

    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: returnToDelete.date ? new Date(returnToDelete.date).toLocaleDateString('fr-FR') : '-' });
    fields.push({ label: 'BON RETOUR N°', value: returnToDelete.bon_retour_number });

    if (returnToDelete.client_name) {
      fields.push({ label: 'Client', value: returnToDelete.client_name });
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${returnToDelete.items?.length || 0}`, bold: true });

    // Add all articles
    (returnToDelete.items || []).forEach((item, index) => {
      fields.push({
        label: `Article ${index + 1}`,
        value: `${item.article_reference} - ${item.article_designation}`,
      });
      fields.push({
        label: `  Quantité`,
        value: `${formatNumber(item.quantity, 2)}`,
      });
      fields.push({
        label: `  Prix Unitaire`,
        value: `${formatNumber(item.unit_price)} MAD`,
      });
      fields.push({
        label: `  Total`,
        value: `${formatNumber(item.total)} MAD`,
      });
    });

    // Grand total
    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GÉNÉRAL',
      value: `${formatNumber(returnToDelete.grand_total)} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePrint = (bonRetourNumber: string, id?: number) => {
    openPreview({
      getBlob: async () => {
        const { generateStockReturnPDF } = await import('../utils/pdfGenerator');
        return (await generateStockReturnPDF(bonRetourNumber, true)) as Blob;
      },
      fileName: `${bonRetourNumber}.pdf`,
    });
    if (id) auditLogsAPI.logPdfDownload('StockReturn', id, `Aperçu PDF - ${bonRetourNumber}`);
  };

  // Create flat array with grouping metadata for rendering (rowSpan approach)
  const renderData: Array<{ stockReturn: StockReturnNew; item: StockReturnItem; isFirstInBon: boolean; bonRowSpan: number }> = [];
  stockReturns.forEach((stockReturn) => {
    const items = stockReturn.items || [];
    items.forEach((item, index) => {
      renderData.push({
        stockReturn,
        item,
        isFirstInBon: index === 0,
        bonRowSpan: items.length || 1,
      });
    });
    // If no items, still show the return row
    if (items.length === 0) {
      renderData.push({
        stockReturn,
        item: {} as StockReturnItem,
        isFirstInBon: true,
        bonRowSpan: 1,
      });
    }
  });

  // Create flat array for deleted returns
  const deletedRenderData: Array<{ stockReturn: StockReturnNew; item: StockReturnItem; isFirstInBon: boolean; bonRowSpan: number }> = [];
  deletedStockReturns.forEach((stockReturn) => {
    const items = stockReturn.items || [];
    items.forEach((item, index) => {
      deletedRenderData.push({
        stockReturn,
        item,
        isFirstInBon: index === 0,
        bonRowSpan: items.length || 1,
      });
    });
    if (items.length === 0) {
      deletedRenderData.push({
        stockReturn,
        item: {} as StockReturnItem,
        isFirstInBon: true,
        bonRowSpan: 1,
      });
    }
  });

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReturnIcon /> Gestion des Retours de Stock
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gérer les retours de marchandises des clients
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {tabValue === 0 && hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary' }}
              >
                Effacer les filtres
              </Button>
            )}
            {tabValue === 0 && (
            <PermissionButton
              moduleName="Stock"
              operation="add"
              miniModuleName="Stock Return"
              variant="contained"
              color="primary"
              onClick={handleOpenAddDialog}
              startIcon={<AddIcon />}
            >
              Nouveau Retour
            </PermissionButton>
            )}
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Retours Actifs (${totalCount})`} />
            <Tab label="Corbeille" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
        <>
        <Box sx={{ mb: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Rechercher par BON RETOUR N° ou article..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 350 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer sx={getModuleTableTheme('stock')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date
                    <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={STOCK_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    BON RETOUR N°
                    <ColumnFilter type="text" value={filters.bon_retour_number} onChange={(v) => update('bon_retour_number', v)} label="BON RETOUR N°" color={STOCK_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Client
                    <ColumnFilter type="autocomplete" value={filters.client_id} onChange={(v) => update('client_id', v)} options={clientOptions} label="client" color={STOCK_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>Référence</TableCell>
                <TableCell>Article</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Unité</TableCell>
                <TableCell align="right">Quantité</TableCell>
                <TableCell align="right">Prix Unit.</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : renderData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    Aucun retour de stock trouvé
                  </TableCell>
                </TableRow>
              ) : (
                renderData.map(({ stockReturn, item, isFirstInBon, bonRowSpan }, index) => {
                  const isHovered = hoveredBonNumber === stockReturn.bon_retour_number;
                  return (
                    <TableRow
                      key={`${stockReturn.id}-${item.id || index}`}
                      onMouseEnter={() => setHoveredBonNumber(stockReturn.bon_retour_number)}
                      onMouseLeave={() => setHoveredBonNumber(null)}
                      sx={{
                        backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        transition: 'background-color 0.15s ease',
                        '& td': {
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }
                      }}
                    >
                      {/* Date column - only render for first item in BON */}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                          transition: 'background-color 0.15s ease',
                        }}>
                          {stockReturn.date ? new Date(stockReturn.date).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                      )}

                      {/* BON RETOUR N° column - only render for first item in BON */}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                          transition: 'background-color 0.15s ease',
                        }}>
                          <Chip
                            label={stockReturn.bon_retour_number}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                      )}

                      {/* Client column - only render for first item in BON */}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                          transition: 'background-color 0.15s ease',
                        }}>
                          {stockReturn.client_name || '-'}
                        </TableCell>
                      )}

                      {/* Article columns - render for each item */}
                      <TableCell sx={{ backgroundColor: 'inherit' }}>{item.article_reference || '-'}</TableCell>
                      <TableCell sx={{ backgroundColor: 'inherit' }}>{item.article_designation || '-'}</TableCell>
                      <TableCell sx={{ backgroundColor: 'inherit' }}>
                        {item.category && <Chip label={item.category} size="small" variant="filled" />}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'inherit' }}>{item.unit || '-'}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>{formatNumber(item.quantity, 2)}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>{formatCurrency(Number(item.unit_price))}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>
                        <strong>{formatCurrency(Number(item.total))}</strong>
                      </TableCell>

                      {/* Actions column - only render for first item in BON */}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} align="center" sx={{
                          verticalAlign: 'middle',
                          borderLeft: 1, borderLeftColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                          transition: 'background-color 0.15s ease',
                        }}>
                          <Stack direction="row" spacing={0} justifyContent="center">
                            <PermissionButton
                              moduleName="Stock"
                              operation="edit"
                              miniModuleName="Stock Return"
                              component="IconButton"
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(stockReturn)}
                              title="Modifier"
                            >
                              <EditIcon fontSize="small" />
                            </PermissionButton>
                            <PermissionButton
                              moduleName="Stock"
                              operation="view"
                              miniModuleName="Stock Return"
                              component="IconButton"
                              size="small"
                              sx={{ color: '#FF9800' }}
                              onClick={() => handlePrint(stockReturn.bon_retour_number, stockReturn.id)}
                              title="Imprimer"
                            >
                              <PrintIcon fontSize="small" />
                            </PermissionButton>
                            <PermissionButton
                              moduleName="Stock"
                              operation="delete"
                              miniModuleName="Stock Return"
                              component="IconButton"
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(stockReturn)}
                              title="Supprimer"
                            >
                              <DeleteIcon fontSize="small" />
                            </PermissionButton>
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        </>
        )}

        {tabValue === 1 && (
        <>
        <TableContainer sx={getModuleTableTheme('stock')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>BON RETOUR N°</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Référence</TableCell>
                <TableCell>Article</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Unité</TableCell>
                <TableCell align="right">Quantité</TableCell>
                <TableCell align="right">Prix Unit.</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Date de Suppression</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : deletedRenderData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    Aucun retour de stock supprimé
                  </TableCell>
                </TableRow>
              ) : (
                deletedRenderData.map(({ stockReturn, item, isFirstInBon, bonRowSpan }, index) => {
                  const isHovered = hoveredBonNumber === stockReturn.bon_retour_number;
                  return (
                    <TableRow
                      key={`deleted-${stockReturn.id}-${item.id || index}`}
                      onMouseEnter={() => setHoveredBonNumber(stockReturn.bon_retour_number)}
                      onMouseLeave={() => setHoveredBonNumber(null)}
                      sx={{
                        backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        transition: 'background-color 0.15s ease',
                        '& td': {
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }
                      }}
                    >
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }}>
                          {stockReturn.date ? new Date(stockReturn.date).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                      )}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }}>
                          <Chip
                            label={stockReturn.bon_retour_number}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                      )}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }}>
                          {stockReturn.client_name || '-'}
                        </TableCell>
                      )}
                      <TableCell sx={{ backgroundColor: 'inherit' }}>{item.article_reference || '-'}</TableCell>
                      <TableCell sx={{ backgroundColor: 'inherit' }}>{item.article_designation || '-'}</TableCell>
                      <TableCell sx={{ backgroundColor: 'inherit' }}>
                        {item.category && <Chip label={item.category} size="small" variant="filled" />}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'inherit' }}>{item.unit || '-'}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>{formatNumber(item.quantity, 2)}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>{formatCurrency(Number(item.unit_price))}</TableCell>
                      <TableCell align="right" sx={{ backgroundColor: 'inherit' }}>
                        <strong>{formatCurrency(Number(item.total))}</strong>
                      </TableCell>
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} sx={{
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          borderRight: 1, borderRightColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }}>
                          {stockReturn.deleted_at ? new Date(stockReturn.deleted_at).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                      )}
                      {isFirstInBon && (
                        <TableCell rowSpan={bonRowSpan} align="center" sx={{
                          verticalAlign: 'middle',
                          borderLeft: 1, borderLeftColor: 'divider',
                          backgroundColor: isHovered ? 'rgba(33, 150, 243, 0.08) !important' : 'inherit',
                        }}>
                          <IconButton
                            onClick={() => handleRestore(stockReturn)}
                            color="primary"
                            size="small"
                            title="Restaurer"
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={deletedStockReturns.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        </>
        )}
      </Paper>

      <StockReturnAdd
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
        onSuccess={() => {
          handleCloseAddDialog();
          invalidateReturns();
        }}
        articles={articles}
        clients={clients}
      />

      <StockReturnEdit
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        onSuccess={() => {
          handleCloseEditDialog();
          invalidateReturns();
        }}
        returnEntry={editingReturn}
        articles={articles}
        clients={clients}
      />

      <ConfirmationDialog
        open={showDeleteConfirmDialog}
        title="Confirmer la suppression"
        message="Êtes-vous sûr de vouloir supprimer ce BON RETOUR ?"
        fields={getDeleteConfirmationFields()}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirmDialog(false);
          setReturnToDelete(null);
        }}
        confirmText="Supprimer"
        cancelText="Annuler"
        loading={isDeleting}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default StockReturnNewManagement;
